import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  ArrowUpRight,
  BedDouble,
  Bath,
} from "lucide-react";

const PropertyCard = ({ property, index = 0 }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        duration: 0.6,
        delay: index * 0.08,
      }}
      className="group"
    >
      <Link
        to={`/properties/${property.slug}`}
        className="block"
        aria-label={`View ${property.title}`}
      >
        {/* IMAGE */}
        <div className="relative overflow-hidden bg-gray-100 aspect-[4/3]">
          <img
            src={property.coverImage}
            alt={property.title}
            className="
              w-full
              h-full
              object-cover
              transition-transform
              duration-700
              ease-out
              group-hover:scale-105
            "
          />

          {/* subtle overlay */}
          <div
            className="
              absolute
              inset-0
              bg-gradient-to-t
              from-black/30
              via-transparent
              to-transparent
            "
          />

          {/* STATUS */}
          <div className="absolute top-5 left-5">
            <span
              className="
                inline-flex
                items-center
                bg-white/95
                backdrop-blur-sm
                px-4
                py-2
                text-[11px]
                uppercase
                tracking-[0.18em]
                font-semibold
                text-gray-900
              "
            >
              {property.status}
            </span>
          </div>

          {/* ARROW */}
          <div
            className="
              absolute
              bottom-5
              right-5
              w-11
              h-11
              rounded-full
              bg-white
              flex
              items-center
              justify-center
              transition-all
              duration-300
              translate-y-2
              opacity-0
              group-hover:translate-y-0
              group-hover:opacity-100
            "
          >
            <ArrowUpRight
              size={19}
              strokeWidth={1.8}
              className="text-gray-900"
            />
          </div>
        </div>

        {/* CONTENT */}
        <div className="pt-6">
          {/* CATEGORY */}
          <div className="flex items-center justify-between gap-4 mb-3">
            <p
              className="
                text-xs
                uppercase
                tracking-[0.2em]
                font-semibold
                text-blue-600
              "
            >
              {property.category}
            </p>

            {property.publishedAt && (
              <p className="text-xs text-gray-400">
                {new Date(property.publishedAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            )}
          </div>

          {/* TITLE */}
          <h3
            className="
              text-2xl
              md:text-[26px]
              leading-tight
              font-semibold
              text-gray-900
              transition-colors
              duration-300
              group-hover:text-blue-600
            "
          >
            {property.title}
          </h3>

          {/* LOCATION */}
          <div className="flex items-center gap-2 mt-3 text-gray-500">
            <MapPin size={16} strokeWidth={1.7} />

            <span className="text-sm">
              {property.location}
            </span>
          </div>

          {/* DESCRIPTION */}
          <p
            className="
              mt-4
              text-[15px]
              leading-7
              text-gray-600
              line-clamp-2
            "
          >
            {property.excerpt}
          </p>

          {/* PROPERTY META */}
          <div
            className="
              flex
              flex-wrap
              items-center
              gap-x-5
              gap-y-2
              mt-5
              text-sm
              text-gray-600
            "
          >
            {property.bedrooms && (
              <div className="flex items-center gap-2">
                <BedDouble
                  size={17}
                  strokeWidth={1.7}
                />

                <span>
                  {property.bedrooms} Beds
                </span>
              </div>
            )}

            {property.bathrooms && (
              <div className="flex items-center gap-2">
                <Bath
                  size={17}
                  strokeWidth={1.7}
                />

                <span>
                  {property.bathrooms} Baths
                </span>
              </div>
            )}
          </div>

          {/* PRICE + LINK */}
          <div
            className="
              flex
              items-end
              justify-between
              gap-5
              mt-6
              pt-5
              border-t
              border-gray-200
            "
          >
            <div>
              {property.priceLabel && (
                <p
                  className="
                    text-[10px]
                    uppercase
                    tracking-[0.17em]
                    text-gray-400
                    font-semibold
                    mb-1
                  "
                >
                  {property.priceLabel}
                </p>
              )}

              <p className="text-lg font-semibold text-gray-900">
                {property.price}
              </p>
            </div>

            <div
              className="
                flex
                items-center
                gap-2
                text-sm
                font-semibold
                text-gray-900
                group-hover:text-blue-600
                transition-colors
              "
            >
              <span>Read More</span>

              <ArrowUpRight
                size={16}
                strokeWidth={1.8}
              />
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};

export default PropertyCard;